import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Building2, ArrowLeft, User, Mail, Phone,
  Wrench, Bell, Edit, Trash2, Home, FileText, AlertCircle,
  ChevronRight, X
} from 'lucide-react';
import api from '../../api/apiClient';

interface Property {
  id: number;
  address: string;
  type: string;
  bedrooms?: number;
  bathrooms?: number;
  rentAmount?: number;
  rentFrequency?: string;
  status: string;
  tenantName?: string;
  ownerName?: string;
  activeIssues: number;
  currentLease?: {
    id: number;
    startDate: string;
    endDate: string;
    rentAmount: number;
    status: string;
    tenantName?: string;
    tenantEmail?: string;
    tenantPhone?: string;
  };
  owner?: {
    id: number;
    name: string;
    email: string;
    phone?: string;
  };
}

interface Notification {
  id: string;
  type: string;
  title: string;
  message: string;
  status: string;
  createdAt: string;
  urgency?: string;
}

interface MaintenanceRequest {
  id: number;
  category: string;
  description: string;
  urgency: string;
  status: string;
  createdAt: string;
  priority: number;
}

export default function AgentPropertyDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [property, setProperty] = useState<Property | null>(null);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [maintenance, setMaintenance] = useState<MaintenanceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'overview' | 'notifications' | 'maintenance'>('overview');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (id) {
      loadProperty();
      loadNotifications();
      loadMaintenance();
    }
  }, [id]);

  const loadProperty = async () => {
    setLoading(true);
    try {
      const response = await api.get<Property>(`/agent/properties/${id}`);
      setProperty(response);
    } catch (err) {
      console.error('Failed to load property:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await api.get<Notification[]>(`/agent/properties/${id}/notifications`);
      setNotifications(response || []);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  };

  const loadMaintenance = async () => {
    try {
      const response = await api.get<MaintenanceRequest[]>(`/agent/properties/${id}/maintenance`);
      setMaintenance(response || []);
    } catch (err) {
      console.error('Failed to load maintenance:', err);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      await api.delete(`/agent/properties/${id}`);
      navigate('/agent/properties');
    } catch (err: any) {
      console.error('Failed to delete property:', err);
      // Try to extract error message from response
      const errorMessage = err.message || 'Failed to delete property';
      if (errorMessage.includes('ACTIVE_LEASE_EXISTS') || errorMessage.includes('active or pending leases')) {
        setError('Cannot delete property with active or pending leases. Please terminate all leases first.');
      } else if (errorMessage.includes('LEASES_EXIST') || errorMessage.includes('lease history')) {
        setError('Cannot delete property with lease history. You can archive this property instead.');
      } else {
        setError(errorMessage);
      }
      setShowDeleteModal(false);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'OCCUPIED': return 'bg-green-100 text-green-800';
      case 'VACANT': return 'bg-yellow-100 text-yellow-800';
      case 'OPEN': return 'bg-blue-100 text-blue-800';
      case 'ASSIGNED': return 'bg-purple-100 text-purple-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency?.toUpperCase()) {
      case 'EMERGENCY': return 'text-red-600';
      case 'HIGH': return 'text-orange-600';
      case 'MEDIUM': return 'text-yellow-600';
      case 'LOW': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const formatRent = (amount?: number, frequency?: string) => {
    if (!amount) return 'Not set';
    const freq = frequency?.toLowerCase() || 'week';
    return `$${amount.toLocaleString()}/${freq.replace('ly', '')}`;
  };

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8 text-center">
        <p className="text-gray-600">Property not found</p>
        <button onClick={() => navigate('/agent/properties')} className="mt-4 text-secondary hover:text-primary">
          Back to Properties
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button onClick={() => navigate('/agent/properties')} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-gray-900">{property.address}</h1>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(property.status)}`}>
              {property.status}
            </span>
          </div>
          <p className="text-gray-600">{property.type || 'Property'}</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate(`/agent/properties/${id}/edit`)}
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <Edit className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => setShowDeleteModal(true)}
            className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
          >
            <Trash2 className="w-4 h-4" />
            Delete
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
          <span className="text-red-800">{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-600 hover:text-red-800">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`pb-3 font-medium border-b-2 transition-colors ${
              activeTab === 'overview' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('notifications')}
            className={`pb-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'notifications' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Notifications
            {notifications.length > 0 && (
              <span className="px-2 py-0.5 text-xs bg-red-100 text-red-800 rounded-full">{notifications.length}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('maintenance')}
            className={`pb-3 font-medium border-b-2 transition-colors flex items-center gap-2 ${
              activeTab === 'maintenance' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Maintenance
            {maintenance.filter(m => m.status !== 'COMPLETED').length > 0 && (
              <span className="px-2 py-0.5 text-xs bg-orange-100 text-orange-800 rounded-full">
                {maintenance.filter(m => m.status !== 'COMPLETED').length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Property Details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center gap-2 mb-4">
                <Home className="w-5 h-5 text-primary" />
                <h2 className="text-lg font-semibold text-gray-900">Property Details</h2>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium text-gray-900">{property.address}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <p className="font-medium text-gray-900">{property.type || 'Not specified'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Bedrooms</p>
                  <p className="font-medium text-gray-900">{property.bedrooms || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Bathrooms</p>
                  <p className="font-medium text-gray-900">{property.bathrooms || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Rent</p>
                  <p className="font-medium text-gray-900">{formatRent(property.rentAmount, property.rentFrequency)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(property.status)}`}>
                    {property.status}
                  </span>
                </div>
              </div>
            </div>

            {/* Current Lease */}
            {property.currentLease && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <FileText className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-gray-900">Current Lease</h2>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Lease Period</p>
                    <p className="font-medium text-gray-900">
                      {formatDate(property.currentLease.startDate)} - {formatDate(property.currentLease.endDate)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Monthly Rent</p>
                    <p className="font-medium text-gray-900">${property.currentLease.rentAmount?.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(property.currentLease.status)}`}>
                      {property.currentLease.status}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Tenant Info */}
            {property.currentLease && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <User className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-gray-900">Tenant</h2>
                </div>
                <div className="space-y-3">
                  <p className="font-medium text-gray-900">{property.currentLease.tenantName || 'Unknown'}</p>
                  {property.currentLease.tenantEmail && (
                    <a href={`mailto:${property.currentLease.tenantEmail}`} className="flex items-center gap-2 text-sm text-secondary hover:text-primary">
                      <Mail className="w-4 h-4" /> {property.currentLease.tenantEmail}
                    </a>
                  )}
                  {property.currentLease.tenantPhone && (
                    <a href={`tel:${property.currentLease.tenantPhone}`} className="flex items-center gap-2 text-sm text-secondary hover:text-primary">
                      <Phone className="w-4 h-4" /> {property.currentLease.tenantPhone}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Owner Info */}
            {property.owner && (
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-semibold text-gray-900">Owner</h2>
                </div>
                <div className="space-y-3">
                  <p className="font-medium text-gray-900">{property.owner.name}</p>
                  {property.owner.email && (
                    <a href={`mailto:${property.owner.email}`} className="flex items-center gap-2 text-sm text-secondary hover:text-primary">
                      <Mail className="w-4 h-4" /> {property.owner.email}
                    </a>
                  )}
                  {property.owner.phone && (
                    <a href={`tel:${property.owner.phone}`} className="flex items-center gap-2 text-sm text-secondary hover:text-primary">
                      <Phone className="w-4 h-4" /> {property.owner.phone}
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Quick Stats */}
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Stats</h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Active Issues</span>
                  <span className={`font-medium ${property.activeIssues > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {property.activeIssues}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-600">Notifications</span>
                  <span className="font-medium text-gray-900">{notifications.length}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'notifications' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Property Notifications</h2>
          </div>
          {notifications.length === 0 ? (
            <div className="p-12 text-center">
              <Bell className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No notifications</h3>
              <p className="text-gray-500">This property has no active notifications.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {notifications.map(notif => (
                <div key={notif.id} className="p-6 hover:bg-gray-50">
                  <div className="flex items-start gap-4">
                    <div className={`p-2 rounded-lg ${notif.type === 'MAINTENANCE' ? 'bg-orange-100' : 'bg-blue-100'}`}>
                      {notif.type === 'MAINTENANCE' ? (
                        <Wrench className="w-5 h-5 text-orange-600" />
                      ) : (
                        <Bell className="w-5 h-5 text-blue-600" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-medium text-gray-900">{notif.title}</p>
                        {notif.urgency && (
                          <span className={`text-xs font-medium ${getUrgencyColor(notif.urgency)}`}>
                            {notif.urgency}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{notif.message}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className={`px-2 py-0.5 rounded-full ${getStatusColor(notif.status)}`}>
                          {notif.status}
                        </span>
                        <span>{formatDate(notif.createdAt)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'maintenance' && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">Maintenance Requests</h2>
          </div>
          {maintenance.length === 0 ? (
            <div className="p-12 text-center">
              <Wrench className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No maintenance requests</h3>
              <p className="text-gray-500">This property has no maintenance requests.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {maintenance.map(req => (
                <div
                  key={req.id}
                  className="p-6 hover:bg-gray-50 cursor-pointer"
                  onClick={() => navigate(`/agent/maintenance/${req.id}`)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(req.status)}`}>
                          {req.status}
                        </span>
                        <span className={`text-sm font-medium ${getUrgencyColor(req.urgency)}`}>
                          {req.urgency}
                        </span>
                      </div>
                      <p className="font-medium text-gray-900 mb-1">{req.category}</p>
                      <p className="text-sm text-gray-600 line-clamp-2">{req.description}</p>
                      <p className="text-xs text-gray-500 mt-2">{formatDate(req.createdAt)}</p>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Delete Property</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this property? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


